import { useState, useRef } from "react";
import { MapPin, Upload, Edit, X } from "lucide-react";
import { Button } from "../Button";
import StepMarker from "../StepMarker";

export default function CommunityEventFinal({
  formData,
  updateFormData,
  onBack,
  onDone,
  goToStep,
}) {
  const date = formData.date ? new Date(formData.date) : new Date("2024-11-13");
  const day = date.getDate(); // 1–31
  const month = date.toLocaleString("default", { month: "short" }); // e.g. "Nov"
  const year = date.getFullYear(); // e.g. 2024

  const [coverImage, setCoverImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    updateFormData({ tempCommunityImage: file });
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setCoverImage({
        file,
        url: imageUrl,
        name: file.name,
      });
      setImageError(false);
    }
  };

  const handleRemoveImage = () => {
    setCoverImage(null);
    updateFormData({ tempCommunityImage: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleDone = () => {
    if (!coverImage || !formData.tempCommunityImage) {
      setImageError(true);
      return;
    }
    onDone();
  };

  return (
    <div className="p-2">
      <StepMarker currentStep={3} totalSteps={3} onBack={onBack} />

      <div className="flex justify-between items-center mb-2">
        <div>
          <div className="text-sm text-gray-500">Complete the look</div>
          <h2 className="text-xl font-bold">Banner Up, You're Ready!</h2>
        </div>
        <button
          className="flex items-center text-[#7B189F]"
          onClick={() => goToStep(2)}
        >
          <Edit size={16} className="mr-1" />
          <span className="text-sm font-medium">Edit Details</span>
        </button>
      </div>

      {/* Upload Invitation Cover */}
      <div
        className={`bg-gray-100 rounded-lg mb-2 relative border ${
          imageError ? "border-red-500" : "border-gray-200"
        } shadow-sm ${
          coverImage
            ? "p-0 overflow-hidden"
            : "p-8 flex flex-col items-center justify-center"
        }`}
        style={{ height: coverImage ? "220px" : "auto" }}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*"
        />

        {coverImage ? (
          <>
            <img
              src={coverImage.url || "/placeholder.svg"}
              alt="Invitation cover"
              className="w-full h-full object-cover"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-md hover:bg-gray-100 transition-colors"
              aria-label="Remove image"
            >
              <X size={16} className="text-gray-600" />
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border border-gray-300 rounded-lg flex items-center justify-center mb-4">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.5 14.5L11.5 11.5L14.5 14.5L19 9"
                  stroke="#888888"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 3H3C1.89543 3 1 3.89543 1 5V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V5C23 3.89543 22.1046 3 21 3Z"
                  stroke="#888888"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 9C8.10457 9 9 8.10457 9 7C9 5.89543 8.10457 5 7 5C5.89543 5 5 5.89543 5 7C5 8.10457 5.89543 9 7 9Z"
                  stroke="#888888"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <button
              className="flex items-center text-gray-700 font-medium"
              onClick={triggerFileInput}
            >
              <Upload size={16} className="mr-2" />
              Upload Invitation cover*
            </button>
          </>
        )}
      </div>

      {imageError && (
        <p className="text-red-500 text-sm mb-4">
          Invitation cover image is required
        </p>
      )}

      {/* Event Details */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <h3 className="font-bold text-xl mb-1">
          {formData.eventName || "Our Gender Reveal Party"}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Join us as we reveal the exciting news and celebrate with family and
          friends!
        </p>

        <div className="flex items-center mb-2">
          <div className="font-medium mr-8">
            {formData.startTime} - {formData.endTime}
          </div>
          <div className="flex items-center text-gray-600">
            <MapPin size={16} className="mr-1" />
            <span>{formData.venue || "Cesar Chavez Avenue"}</span>
          </div>
        </div>

        <div className="flex items-start">
          <div>
            <div className="font-medium">
              {day} {month}
            </div>
            <div className="text-sm text-gray-500">{year}</div>
          </div>
          <button className="ml-auto bg-white border border-gray-200 rounded-full px-4 py-2 text-sm hover:bg-gray-50 transition-colors">
            Get Direction
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={handleDone}
          className="bg-[#7B189F] hover:bg-[#671485] text-white px-12"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
